/**
 * Custom hook for fetching Scaling Up dashboard data
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, handleApiError } from '@/api/apiClient';
import type { ScalingUpSnapshot } from '@/types/scalingUp';

interface UseScalingUpResult {
  data: ScalingUpSnapshot | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useScalingUp(): UseScalingUpResult {
  const [data, setData] = useState<ScalingUpSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = refresh
        ? '/scaling-up/snapshot?refresh=true'
        : '/scaling-up/snapshot';
      const response = await apiClient.get<{ data: ScalingUpSnapshot }>(url);
      setData(response.data.data);
    } catch (err) {
      try {
        handleApiError(err, 'Failed to load Scaling Up data');
      } catch (handled) {
        setError(
          handled instanceof Error ? handled.message : 'Failed to load data'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, loading, error, refresh };
}
